import { useCallback, useState } from 'react';
import { PersonAdd, Delete, Send } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { getRoleBadgeColor, getRoleDisplayName } from '@utils/roleUtils';
import { useSnackbar } from 'notistack';

import type { AdminInvitationResponse } from '@store/generated/organizations';

import {
  useGetAdminInvitationsQuery,
  useDeleteAdminInvitationMutation,
  useResendAdminInvitationMutation,
} from '../store/hooks';
import { formatDateTime } from '../utils/formatting';
import { AdminInviteUserDialog } from './admin-invite-user-dialog';

interface OrganizationInvitationsSectionProps {
  organizationId: string;
}

function getStatusColor(status: string): 'warning' | 'success' | 'error' | 'default' {
  switch (status) {
    case 'pending':
      return 'warning';
    case 'accepted':
      return 'success';
    case 'expired':
      return 'error';
    default:
      return 'default';
  }
}

function canActOn(status: string): boolean {
  return status === 'pending' || status === 'expired';
}

export function OrganizationInvitationsSection({ organizationId }: OrganizationInvitationsSectionProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading, refetch } = useGetAdminInvitationsQuery({
    organizationId,
    includeExpired: true,
  });
  const [deleteInvitation] = useDeleteAdminInvitationMutation();
  const [resendInvitation] = useResendAdminInvitationMutation();

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});

  const setLoading = useCallback((key: string, loading: boolean) => {
    setLoadingStates(prev => ({ ...prev, [key]: loading }));
  }, []);

  const handleDelete = useCallback(
    async (invitationId: string) => {
      setLoading(`delete-${invitationId}`, true);
      try {
        await deleteInvitation({ organizationId, invitationId }).unwrap();
        enqueueSnackbar('Invitation deleted', { variant: 'success' });
        refetch();
      } catch (error: any) {
        enqueueSnackbar(error?.data?.message || 'Failed to delete invitation', { variant: 'error' });
      } finally {
        setLoading(`delete-${invitationId}`, false);
      }
    },
    [deleteInvitation, organizationId, enqueueSnackbar, refetch, setLoading],
  );

  const handleResend = useCallback(
    async (invitationId: string) => {
      setLoading(`resend-${invitationId}`, true);
      try {
        await resendInvitation({ organizationId, invitationId }).unwrap();
        enqueueSnackbar('Invitation resent', { variant: 'success' });
        refetch();
      } catch (error: any) {
        enqueueSnackbar(error?.data?.message || 'Failed to resend invitation', { variant: 'error' });
      } finally {
        setLoading(`resend-${invitationId}`, false);
      }
    },
    [resendInvitation, organizationId, enqueueSnackbar, refetch, setLoading],
  );

  const handleInviteSuccess = useCallback(() => {
    setInviteDialogOpen(false);
    refetch();
  }, [refetch]);

  const invitations = data?.items ?? [];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Card elevation={1} sx={{ borderRadius: 2, overflow: 'hidden', border: 1, borderColor: 'grey.200' }}>
        <CardHeader
          title="Invitations"
          titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
          action={
            <Button
              variant="contained"
              size="small"
              startIcon={<PersonAdd />}
              onClick={() => setInviteDialogOpen(true)}
            >
              Invite User
            </Button>
          }
        />
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {invitations.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No invitations found
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table aria-label="Organization invitations">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: 'grey.50', fontWeight: 600, fontSize: '0.875rem' }}>Email</TableCell>
                    <TableCell sx={{ bgcolor: 'grey.50', fontWeight: 600, fontSize: '0.875rem' }}>Roles</TableCell>
                    <TableCell sx={{ bgcolor: 'grey.50', fontWeight: 600, fontSize: '0.875rem' }}>Status</TableCell>
                    <TableCell sx={{ bgcolor: 'grey.50', fontWeight: 600, fontSize: '0.875rem' }}>Created</TableCell>
                    <TableCell sx={{ bgcolor: 'grey.50', fontWeight: 600, fontSize: '0.875rem' }}>Expires</TableCell>
                    <TableCell align="right" sx={{ bgcolor: 'grey.50', fontWeight: 600, fontSize: '0.875rem' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody sx={{ '& tr:last-child td': { borderBottom: 'none' } }}>
                  {invitations.map((invitation, index) => (
                    <InvitationRow
                      key={invitation.invitation_id}
                      invitation={invitation}
                      index={index}
                      onDelete={handleDelete}
                      onResend={handleResend}
                      isDeleting={loadingStates[`delete-${invitation.invitation_id}`] || false}
                      isResending={loadingStates[`resend-${invitation.invitation_id}`] || false}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <AdminInviteUserDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        onSuccess={handleInviteSuccess}
        organizationId={organizationId}
      />
    </>
  );
}

interface InvitationRowProps {
  invitation: AdminInvitationResponse;
  index: number;
  onDelete: (invitationId: string) => void;
  onResend: (invitationId: string) => void;
  isDeleting: boolean;
  isResending: boolean;
}

function InvitationRow({
  invitation,
  index,
  onDelete,
  onResend,
  isDeleting,
  isResending,
}: InvitationRowProps) {
  const statusColor = getStatusColor(invitation.status);
  const showActions = canActOn(invitation.status);

  return (
    <TableRow sx={{ bgcolor: index % 2 === 0 ? 'transparent' : 'grey.50' }}>
      <TableCell>
        <Typography variant="body2">{invitation.invited_email}</Typography>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {(invitation.preset_roles ?? []).map(role => {
            const badgeColor = getRoleBadgeColor(role);
            return (
              <Chip
                key={role}
                label={getRoleDisplayName(role)}
                size="small"
                sx={{
                  height: 24,
                  fontWeight: 600,
                  '& .MuiChip-label': { fontSize: '0.75rem' },
                  backgroundColor: theme => {
                    if (badgeColor === 'default') return alpha(theme.palette.grey[500], 0.1);
                    return alpha(theme.palette[badgeColor].main, 0.1);
                  },
                  color: theme => {
                    if (badgeColor === 'default') return theme.palette.grey[700];
                    return theme.palette[badgeColor].dark;
                  },
                  border: '1px solid',
                  borderColor: theme => {
                    if (badgeColor === 'default') return theme.palette.grey[500];
                    return theme.palette[badgeColor].dark;
                  },
                }}
              />
            );
          })}
        </Box>
      </TableCell>
      <TableCell>
        <Chip
          label={invitation.status}
          size="small"
          color={statusColor}
          variant={statusColor === 'default' ? 'filled' : 'outlined'}
          sx={{ height: 24, fontWeight: 600, '& .MuiChip-label': { fontSize: '0.75rem', textTransform: 'capitalize' } }}
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {formatDateTime(invitation.created_at)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {formatDateTime(invitation.expires_at)}
        </Typography>
      </TableCell>
      <TableCell align="right">
        {showActions && (
          <>
            <Tooltip title="Resend Invitation">
              <span>
                <IconButton
                  size="small"
                  onClick={() => onResend(invitation.invitation_id)}
                  disabled={isResending}
                  aria-label="Resend invitation"
                >
                  {isResending ? <CircularProgress size={18} /> : <Send fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete Invitation">
              <span>
                <IconButton
                  size="small"
                  onClick={() => onDelete(invitation.invitation_id)}
                  disabled={isDeleting}
                  aria-label="Delete invitation"
                >
                  {isDeleting ? <CircularProgress size={18} /> : <Delete fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
      </TableCell>
    </TableRow>
  );
}
